import React from 'react';
import { PureQueryOptions } from '@apollo/client';
import styled from '@emotion/styled';
import TimeEntryEmployeeSmart from '../TimeEntry/TimeEntryEmployeeSmart';
import TimeEntryManagerSmart from '../TimeEntry/TimeEntryManagerSmart';
import DayHeader from '../../../../components/TimeSheet/DayCard/DayHeader';
import { CostCenterProps } from '..';
import { isManagerOrCanChangeOwnTimeEntries } from '../logic/mapping';
import {
  calculateTotalDuration,
  DayWithTimeEntries,
  EmployeeType,
  TimeEntryItem,
  TimeEntryReadOnlyReason,
} from '../logic';
import {
  calculateSavedProjectTimeEntriesByTimeEntryId,
  calculateTotalProjectTimeEntriesMinutes,
  mapTimeEntriesToTimeEntryProps,
} from '../logic/projectTimeEntries';
import { ProjectWithStatus } from '../../../Projects/types';
import EmptyTimeEntryContainer from '../components/EmptyTimeEntryContainer';

type Props = {
  day: DayWithTimeEntries;
  costCenters: readonly CostCenterProps[];
  readonlyReasons: TimeEntryReadOnlyReason[];
  employeeType: EmployeeType;
  timeEntries: TimeEntryItem[];
  refetchQueriesForEmployeeMode?: PureQueryOptions[];
  onAddNewTimeEntryClick: () => void;
  onCancelCreatingNewTimeEntry: () => void;
  projects?: ProjectWithStatus[];
  isTimeTrackingWithProjects: boolean;
};

const DayTimeEntriesDumb: React.FC<React.PropsWithChildren<Props>> = ({
  day,
  costCenters,
  employeeType,
  readonlyReasons,
  timeEntries,
  refetchQueriesForEmployeeMode,
  onCancelCreatingNewTimeEntry,
  onAddNewTimeEntryClick,
  projects,
  isTimeTrackingWithProjects,
}) => {
  const isManagerMode = isManagerOrCanChangeOwnTimeEntries(employeeType);

  const [
    currentProjectTimeEntriesByTimeEntryId,
    setCurrentProjectTimeEntriesByTimeEntryId,
  ] = React.useState<Record<string, number>>({});

  const handleProjectTimeEntriesMinutesChange = React.useCallback(
    (timeEntryId: string | null, minutes: number) => {
      if (!timeEntryId) return;
      setCurrentProjectTimeEntriesByTimeEntryId((prev) => {
        if (prev[timeEntryId] === minutes) {
          return prev;
        }
        return {
          ...prev,
          [timeEntryId]: minutes,
        };
      });
    },
    [],
  );

  const savedProjectTimeEntriesByTimeEntryId = React.useMemo(
    () => calculateSavedProjectTimeEntriesByTimeEntryId(day.projectTimeEntries),
    [day.projectTimeEntries],
  );

  const totalProjectTimeEntriesMinutes = React.useMemo(
    () =>
      calculateTotalProjectTimeEntriesMinutes(
        currentProjectTimeEntriesByTimeEntryId,
        savedProjectTimeEntriesByTimeEntryId,
      ),
    [
      currentProjectTimeEntriesByTimeEntryId,
      savedProjectTimeEntriesByTimeEntryId,
    ],
  );

  const timeEntriesForDay = React.useMemo(
    () => mapTimeEntriesToTimeEntryProps(timeEntries),
    [timeEntries],
  );

  return (
    <>
      <DayHeader
        dayInfo={day}
        absences={day.absences}
        holidays={day.holidays}
        calendarPeriods={day.calendarPeriods}
        warnings={day.warnings}
        trackedMinutes={calculateTotalDuration(timeEntries)}
        projectTimeEntriesMinutes={totalProjectTimeEntriesMinutes}
        commentsCount={day.commentsCount}
        showTrackedTime={true}
        showProjectTrackedTime={isTimeTrackingWithProjects}></DayHeader>
      {timeEntries.length === 0 && (
        <EmptyTimeEntryContainer
          readonlyReasons={readonlyReasons}
          onAddNewTimeEntryClick={onAddNewTimeEntryClick}
        />
      )}
      {timeEntries.map((item, index) => {
        const isLastEntry = index === timeEntries.length - 1;
        const restrictedFutureEntry = readonlyReasons.includes(
          'restrictedFutureEntries',
        );
        return !isManagerMode ? (
          <TimeEntryEmployeeSmart
            key={item.uniqueId}
            costCenters={costCenters}
            autoBreakRules={day.autoBreakRules}
            readonlyReasons={readonlyReasons}
            value={item}
            allTimeEntries={timeEntries}
            refetchQueriesForEmployeeMode={refetchQueriesForEmployeeMode}
            onCancelCreatingNewTimeEntry={onCancelCreatingNewTimeEntry}
            onAddNewTimeEntryClick={onAddNewTimeEntryClick}
            isLastEntry={isLastEntry}
            projectTimeEntries={day.projectTimeEntries}
            restrictedFutureEntries={restrictedFutureEntry}
            projects={projects}
            onProjectTimeEntriesMinutesChange={
              handleProjectTimeEntriesMinutesChange
            }
            timeEntriesForDay={timeEntriesForDay}
            showTimeEntries={false}
            isTimeTrackingWithProjects={isTimeTrackingWithProjects}
          />
        ) : (
          <TimeEntryManagerSmartContainer key={item.uniqueId}>
            <TimeEntryManagerSmart
              costCenters={costCenters}
              autoBreakRules={day.autoBreakRules}
              readonlyReasons={readonlyReasons}
              employeeType={employeeType}
              value={item}
              allTimeEntries={timeEntries}
              refetchQueriesForEmployeeMode={refetchQueriesForEmployeeMode}
              onCancelCreatingNewTimeEntry={onCancelCreatingNewTimeEntry}
              onAddNewTimeEntryClick={onAddNewTimeEntryClick}
              isLastEntry={isLastEntry}
              projectTimeEntries={day.projectTimeEntries}
              restrictedFutureEntries={restrictedFutureEntry}
              projects={projects}
              onProjectTimeEntriesMinutesChange={
                handleProjectTimeEntriesMinutesChange
              }
              timeEntriesForDay={timeEntriesForDay}
              showTimeEntries={false}
              isTimeTrackingWithProjects={isTimeTrackingWithProjects}
            />
          </TimeEntryManagerSmartContainer>
        );
      })}
    </>
  );
};

const TimeEntryManagerSmartContainer = styled('div')({
  marginBottom: '2.2rem',
});

export default DayTimeEntriesDumb;
